import { useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import type { SubscriptionSummary } from '../../auth/types';
import { formatDate } from '../api';

/** Shown above every tenant page while on a trial, after a payment is due, or when the workspace is read-only. */
export function SubscriptionBanner() {
  const { me } = useAuth();
  // Day count fixed at mount; the banner does not need to tick.
  const [now] = useState(() => Date.now());
  const sub: SubscriptionSummary | null | undefined = me?.subscription;
  if (!sub) return null;
  const days = (iso: string | null) =>
    iso ? Math.max(0, Math.ceil((new Date(iso).getTime() - now) / 86_400_000)) : null;
  let text: string | null = null;
  let tone = 'bg-brand-50 text-brand-800 dark:bg-brand-900/40 dark:text-brand-100';
  if (sub.readOnly) {
    text = 'Your subscription has ended. The workspace is read-only until the plan is renewed.';
    tone = 'bg-danger-soft text-danger';
  } else if (sub.status === 'past_due') {
    text = `Payment is due. Everything keeps working until ${formatDate(sub.graceEndsAt)}, then the workspace becomes read-only.`;
    tone = 'bg-warning-soft text-warning';
  } else if (sub.status === 'trialing') {
    const left = days(sub.trialEndsAt);
    text = `Free trial of ${sub.plan?.name ?? 'your plan'}: ${left === null ? '' : `${left} day${left === 1 ? '' : 's'} left`}.`;
  }
  if (!text) return null;
  const canManage = Boolean(me?.permissions['billing.manage']);
  return (
    <div role="status" className={`flex flex-wrap items-center gap-3 px-4 py-2 text-sm ${tone}`}>
      <span className="flex-1">{text}</span>
      {canManage ? (
        <Link to="/app/settings/billing" className="font-medium underline">
          Plan & billing
        </Link>
      ) : null}
    </div>
  );
}
